import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { breakpoints } from '@avangard/ui/utils';

import { CommonHelper } from '@helpers';
import { ButtonAdd, Hint } from '@modules/layout/atoms';
import { ListToolbar } from '@modules/layout/moleculas';
import { useCurrentUser } from '@modules/user/hooks';
import { StudentCreateFormModal } from '@modules/students/organisms';
import { StudentsAddMode } from '@modules/types/graphql';

const HintBlock = styled.div`
    display: inline-flex;
    margin-inline-start: auto;

    ${breakpoints.down(400)} {
        width: 100%;
        margin-inline-start: 0;
        margin-top: 6px;
    }
`;

const StudentsListToolbar = (): React.ReactElement => {
    const { t } = useTranslation(['common', 'students']);

    const { currentUser } = useCurrentUser();

    const [openStudentCreateFormModal, setStudentCreateFormModal] = React.useState(false);

    const handleOpenStudentCreateFormModal = (): void => setStudentCreateFormModal(true);
    const handleCloseStudentCreateFormModal = (): void => setStudentCreateFormModal(false);

    const hasLimitStudents = currentUser.school.studentsAddMode === StudentsAddMode.limited;
    const canCreateStudent = currentUser.isSchool() ? currentUser.school.canCreateStudent() : true;

    const displayCurrentStudentsCount = CommonHelper.createCountFormatter(
        currentUser.school.currentStudentsCount,
        [
            t('common:blocks.students.one'),
            t('common:blocks.students.some'),
            t('common:blocks.students.multiple'),
        ],
    );

    const displayCurrentStudentsLabel = CommonHelper.createCountFormatter(
        currentUser.school.currentStudentsCount,
        [
            t('common:blocks.added.one'),
            t('common:blocks.added.some'),
            t('common:blocks.added.multiple'),
        ],
        { withNumber: false },
    );

    const displayHint =
        currentUser.school.maxStudentsCount > 0 && hasLimitStudents
            ? t('students:blocks.added.range', {
                  currentCount: currentUser.school.currentStudentsCount,
                  maxCount: currentUser.school.maxStudentsCount,
              })
            : `${displayCurrentStudentsLabel} ${displayCurrentStudentsCount}`;

    return (
        <ListToolbar>
            <ButtonAdd disabled={!canCreateStudent} onClick={handleOpenStudentCreateFormModal}>
                {t('students:add_student.title')}
            </ButtonAdd>

            {currentUser.isSchool() ? (
                <HintBlock>
                    <Hint dense>{displayHint}</Hint>
                </HintBlock>
            ) : null}

            {canCreateStudent ? (
                <StudentCreateFormModal
                    open={openStudentCreateFormModal}
                    onClose={handleCloseStudentCreateFormModal}
                />
            ) : null}
        </ListToolbar>
    );
};

export { StudentsListToolbar };
